import React, { useEffect, useRef, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import '@toast-ui/editor/dist/toastui-editor.css';

const ToastUIEditor = lazy(() =>
  import('@toast-ui/react-editor').then(module => ({
    default: module.Editor
  }))
);

const LoadingEditor = ({ loadingText }) => (
  <div className="d-flex flex-row justify-content-start gap-1 align-items-center text-light">
    <Spinner as="span" animation="grow" size="sm" variant="light" />
    {loadingText}
  </div>
);

const InputEditorComponent = ({
  value = '',
  onChange,
  dataTestId = 'editor',
  placeholder = ''
}) => {
  const { t } = useTranslation(['backoffice']);
  const editorRef = useRef(null);
  const lastValueRef = useRef('');

  useEffect(() => {
    const applyValue = () => {
      const instance = editorRef.current?.getInstance?.();
      if (instance && value !== instance.getHTML()) {
        instance.setHTML(value || '');
        lastValueRef.current = value;
      }
    };

    const interval = setInterval(() => {
      if (editorRef.current?.getInstance?.()) {
        applyValue();
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [value]);

  const handleChange = () => {
    const html = editorRef.current?.getInstance?.().getHTML?.();
    if (html !== undefined && html !== lastValueRef.current) {
      lastValueRef.current = html;
      onChange(html);
    }
  };

  return (
    <div data-testid={dataTestId}>
      <Suspense fallback={<LoadingEditor loadingText={t('loadingEditor')} />}>
        <ToastUIEditor
          ref={editorRef}
          previewStyle="vertical"
          height="500px"
          initialEditType="wysiwyg"
          hideModeSwitch
          placeholder={placeholder}
          usageStatistics={false}
          toolbarItems={[
            ['heading', 'bold', 'italic', 'strike'],
            ['hr', 'quote'],
            ['ul', 'ol', 'task'],
            ['table', 'image', 'link']
          ]}
          onChange={handleChange}
        />
      </Suspense>
    </div>
  );
};

InputEditorComponent.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  dataTestId: PropTypes.string,
  placeholder: PropTypes.string
};

export default InputEditorComponent;
